/**
 * ProductDetailHero - Top section of the PDP page.
 * Left column: product info, availability badge, actions (add-to-cart / request).
 * Right column: ImageCarousel.
 *
 * Contact triggers (contextual, from LIVE-APP-CAPTURE 7e):
 *   - "By request only" → request-order topic
 *   - "Request availability" → request-availability topic
 *   - "Contact us" link → product-info topic
 *   - Outphased → request-alternate topic
 *
 * Props:
 *   product       - product object
 *   fieldConfig   - array from getPDPFieldConfig (passed through, not used here)
 *   userState     - 'guest' | 'employee-viewer' | 'customer' | 'employee-admin'
 *   availability  - { pics, isSellable } | null
 */
import { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import ImageCarousel from '@components/product/ImageCarousel';
import RegionAvailabilityBadge from '@components/wizard/RegionAvailabilityBadge';
import { useContactModal } from '@contexts/ContactModalContext';
import { isCommerceEnabledForProductLine, getProductDisplayName } from '@data/index';

export default function ProductDetailHero({ product, fieldConfig, userState, availability, onAddToFavourites, overlayContent }) {
  const [quantity, setQuantity] = useState(1);
  const { openContact } = useContactModal();

  const pics = availability?.pics;
  const isSellable = availability?.isSellable;
  const isGuest = userState === 'guest';
  const isCustomerLike = userState === 'customer' || userState === 'employee-admin';
  const isCommerceEnabled = isCommerceEnabledForProductLine(product?.productLine);
  const isViewer = userState === 'employee-viewer';

  // D5 — overlay description takes priority over PGMJ/PGMN so the PDP
  // shows the customer-facing marketing paragraph from Sandvik.com when
  // available, then falls back to the internal PIM fields.
  const descriptionText = overlayContent?.description || product.overview || product.shortDescPgmn || product.descriptionPGMJ || product.descriptionPGMN;
  const sourceUrl = overlayContent?.sourceUrl;

  // Build contact context from current product
  const contactContext = {
    productName: product.description,
    sku: product.itemCode,
    quantity: quantity,
  };

  const handleContactClick = (topicId) => {
    openContact(topicId, contactContext);
  };

  // --- Availability badge (legacy pics-based: by request / outphased) ---
  const renderBadge = () => {
    if (isGuest || !pics) return null;
    if (pics === 'N') {
      return (
        <span className="inline-block bg-sandvik-orange/10 text-sandvik-orange text-sm px-3 py-1 mb-3">
          By request only
        </span>
      );
    }
    if (pics === 'U') {
      return (
        <span className="inline-block bg-n2 text-n6 text-sm px-3 py-1 mb-3">
          Outphased
        </span>
      );
    }
    return null;
  };

  // --- Quantity selector ---
  const renderQuantitySelector = () => (
    <div className="flex items-center border border-n3 rounded overflow-hidden mb-3">
      <button
        type="button"
        className="bg-n1 px-3 py-2 text-n6 hover:bg-n2 font-normal"
        onClick={() => setQuantity((q) => Math.max(1, q - 1))}
        aria-label="Decrease quantity"
      >
        &minus;
      </button>
      <input
        type="number"
        className="w-16 text-center border-x border-n3 py-2 font-sandvik-text"
        value={quantity}
        min={1}
        onChange={(e) => {
          const val = parseInt(e.target.value, 10);
          if (!isNaN(val) && val >= 1) setQuantity(val);
        }}
        aria-label="Quantity"
      />
      <button
        type="button"
        className="bg-n1 px-3 py-2 text-n6 hover:bg-n2 font-normal"
        onClick={() => setQuantity((q) => q + 1)}
        aria-label="Increase quantity"
      >
        +
      </button>
    </div>
  );

  // --- Hero actions ---
  const renderActions = () => {
    if (isGuest) {
      return (
        <button onClick={() => handleContactClick('product-info')}
          className="text-electric-blue hover:underline font-sandvik-text font-medium">
          Contact us &rsaquo;
        </button>
      );
    }

    if (isViewer) {
      return (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => onAddToFavourites?.(product)}
            className="text-left text-electric-blue hover:underline font-sandvik-text font-medium bg-transparent border-none p-0 cursor-pointer"
          >
            Add to wishlist
          </button>
        </div>
      );
    }

    if (isCustomerLike) {
      return (
        <div className="flex flex-col gap-3">
          {isSellable && pics !== 'N' && pics !== 'U' && isCommerceEnabled ? (
            <>
              {renderQuantitySelector()}
              <button
                type="button"
                className="bg-sandvik-blue text-white px-8 py-3 rounded font-sandvik-text font-medium hover:bg-sandvik-blue/90 transition-colors"
              >
                Add to cart
              </button>
            </>
          ) : !isCommerceEnabled ? (
            <button
              onClick={() => handleContactClick('product-info')}
              type="button"
              className="bg-electric-blue text-white px-8 py-3 rounded font-sandvik-text font-medium hover:bg-electric-blue/90 transition-colors"
            >
              Contact us for pricing &rsaquo;
            </button>
          ) : pics === 'N' ? (
            <>
              {renderQuantitySelector()}
              <button onClick={() => handleContactClick('request-order')}
                type="button"
                className="bg-vibrant-orange text-white px-8 py-3 rounded font-sandvik-text font-medium hover:bg-vibrant-orange/90 transition-colors">
                By request only
              </button>
            </>
          ) : pics === 'U' ? (
            <button onClick={() => handleContactClick('request-alternate')}
              type="button"
              className="text-electric-blue hover:underline font-sandvik-text font-medium">
              Contact us for alternatives &rsaquo;
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => onAddToFavourites?.(product)}
            className="text-left text-electric-blue hover:underline font-sandvik-text font-medium bg-transparent border-none p-0 cursor-pointer"
          >
            Add to wishlist
          </button>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 py-8">
      {/* Left column - product info + actions */}
      <div>
        {renderBadge()}

        {/* Region-availability banner is a commerce concept — hide entirely
            on non-commerce product lines (rotary, raiseboring, services).
            Contact-us is the only path for those, so the banner adds noise. */}
        {isCommerceEnabled && (
          <div className="mb-3">
            <RegionAvailabilityBadge itemCode={product.itemCode} />
          </div>
        )}

        <h1 className="font-sandvik-headline font-normal text-2xl lg:text-3xl text-carbon-black mb-2">
          {getProductDisplayName(product)}
        </h1>

        <p className="text-n6 font-sandvik-text text-sm mb-4">
          Part Number: {product.itemCode}
        </p>

        {sourceUrl && (
          <a
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-n2 text-electric-blue px-4 py-2 mb-6 hover:border-electric-blue hover:bg-electric-blue/5 transition-colors text-sm font-sandvik-text"
          >
            Read more on Sandvik.com
            <ExternalLink size={14} />
          </a>
        )}

        {descriptionText && (
          <p className="text-n7 font-sandvik-text leading-relaxed mb-6">
            {descriptionText}
          </p>
        )}

        {(product.leadTime || product.minBatchSize) && (
          <div className="flex flex-col gap-1 text-sm text-n6 font-sandvik-text mb-6">
            {product.minBatchSize && <span>Min batch size: {product.minBatchSize}</span>}
            {product.leadTime && <span>Lead time: {product.leadTime}</span>}
          </div>
        )}

        {renderActions()}

        {/* Contextual contact links below actions.
            For non-commerce lines (rotary), hide the availability / sellability
            enquiry links — those are commerce-adjacent. Keep the generic
            "Product information" link since it's just contact-us for info. */}
        {!isGuest && (
          <div className="flex flex-wrap gap-4 mt-4 pt-3 border-t border-n2">
            {isCommerceEnabled && (
              <>
                <button onClick={() => handleContactClick('request-availability')}
                  className="text-electric-blue hover:underline font-sandvik-text text-sm flex items-center gap-1">
                  <span className="material-symbols-outlined text-sm">inventory</span>
                  Request availability
                </button>
                <button onClick={() => handleContactClick('product-sellability')}
                  className="text-electric-blue hover:underline font-sandvik-text text-sm flex items-center gap-1">
                  <span className="material-symbols-outlined text-sm">sell</span>
                  Sellability enquiry
                </button>
              </>
            )}
            <button onClick={() => handleContactClick('product-info')}
              className="text-electric-blue hover:underline font-sandvik-text text-sm flex items-center gap-1">
              <span className="material-symbols-outlined text-sm">info</span>
              Product information
            </button>
          </div>
        )}
      </div>

      {/* Right column - image carousel */}
      <div>
        <ImageCarousel
          images={product.images || []}
          productName={product.description}
          categorySlug={product.subcategory}
        />
      </div>
    </div>
  );
}
